/**
 * manager-product.js
 * Gerencia quick-add ao carrinho nos cards de produto.
 * Disponivel como window.ProductManager.
 *
 * Dependencias (devem carregar antes):
 *   - window.CartManager    (manager-cart.js)
 *   - window.ButtonFeedback (manager-global-button-feedback.js)
 */

class ProductManager {
  constructor() {
    this._onClick = this._onClick.bind(this);
    this.cardSelector = '[data-product-card]';
    document.addEventListener('click', this._onClick);

    this.init();
  }

  init(scope = document) {
    const cards = scope.matches?.(this.cardSelector)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(this.cardSelector) || []);

    cards.forEach((card) => {
      if (card.dataset.productCardReady === 'true') return;

      if (this._isCarouselMode(card)) {
        this._setupCarousel(card);
      }

      this._resetSizeSelection(card);
      this._syncAddButton(card);

      if (this._isCarouselMode(card)) {
        this._syncCarousel(card, card.dataset.selectedHandle || '');
      }

      card.dataset.productCardReady = 'true';
    });
  }

  _onClick(event) {
    const popupCloseBtn = event.target.closest('[data-size-popup-close]');
    if (popupCloseBtn) {
      const card = popupCloseBtn.closest('[data-product-card]');
      if (card) this._setSizePopupState(card, false);
      return;
    }

    const addToggleBtn = event.target.closest('[data-product-add-toggle]');
    if (addToggleBtn) {
      const card = addToggleBtn.closest('[data-product-card]');
      if (card && this._isMobileQuickAddCard(card)) {
        event.preventDefault();
        const popup = card.querySelector('[data-size-popup]');
        const isOpen = popup ? !popup.hasAttribute('hidden') : false;
        this._closeAllSizePopups(card);
        this._setSizePopupState(card, !isOpen);
        return;
      }
    }

    const carouselPrevBtn = event.target.closest('[data-product-card-carousel-prev]');
    if (carouselPrevBtn) {
      event.preventDefault();
      const card = carouselPrevBtn.closest('[data-product-card]');
      if (card) this._stepCarousel(card, -1);
      return;
    }

    const carouselNextBtn = event.target.closest('[data-product-card-carousel-next]');
    if (carouselNextBtn) {
      event.preventDefault();
      const card = carouselNextBtn.closest('[data-product-card]');
      if (card) this._stepCarousel(card, 1);
      return;
    }

    const swatchBtn = event.target.closest('[data-product-card-swatch]');
    if (swatchBtn) {
      const card = swatchBtn.closest('[data-product-card]');
      if (card) this._selectColor(card, swatchBtn);
      return;
    }

    const sizeBtn = event.target.closest('[data-size-option]');
    if (sizeBtn) {
      const card = sizeBtn.closest('[data-product-card]');
      if (card) this._selectSize(card, sizeBtn);
      return;
    }

    const addBtn = event.target.closest('[data-product-card-add]');
    if (addBtn) {
      const card = addBtn.closest('[data-product-card]');
      if (card) this._handleAddToCart(card, addBtn);
      return;
    }

    if (!event.target.closest('[data-size-popup-panel]')) {
      this._closeAllSizePopups();
    }
  }

  _isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  _isMobileQuickAddCard(card) {
    return this._isMobileViewport() && card?.classList.contains('productCard--plpMobile') && card.querySelector('[data-size-popup]');
  }

  _setSizePopupState(card, isOpen) {
    const popup = card?.querySelector('[data-size-popup]');
    const toggle = card?.querySelector('[data-product-add-toggle]');
    if (!popup) return;

    popup.hidden = !isOpen;
    popup.classList.toggle('is-open', isOpen);
    toggle?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  _closeAllSizePopups(exceptCard = null) {
    document.querySelectorAll('.productCard--plpMobile[data-product-card]').forEach((card) => {
      if (card !== exceptCard) this._setSizePopupState(card, false);
    });
  }

  _isCarouselMode(card) {
    return card.dataset.mediaMode === 'color_carousel';
  }

  _selectColor(card, swatchBtn) {
    const handle = swatchBtn.dataset.handle;
    if (!handle) return;

    card.dataset.selectedHandle = handle;
    card.dataset.selectedVariantId = swatchBtn.dataset.defaultVariantId || '';

    card.querySelectorAll('[data-product-card-swatch]').forEach((button) => {
      button.classList.toggle('is-active', button === swatchBtn);
    });

    const titleLink = card.querySelector('[data-product-card-title-link]');
    if (titleLink && swatchBtn.dataset.url) {
      titleLink.setAttribute('href', swatchBtn.dataset.url);
    }

    const mediaLink = card.querySelector('[data-product-card-media-link]');
    if (!this._isCarouselMode(card) && mediaLink && swatchBtn.dataset.url) {
      mediaLink.setAttribute('href', swatchBtn.dataset.url);
    }

    const title = card.querySelector('[data-product-card-title]');
    if (title && swatchBtn.dataset.title) {
      title.textContent = swatchBtn.dataset.title;
    }

    const moreColorsLink = card.querySelector('[data-product-card-more-colors]');
    if (moreColorsLink && swatchBtn.dataset.url) {
      moreColorsLink.setAttribute('href', swatchBtn.dataset.url);
    }

    if (!this._isCarouselMode(card)) {
      const primaryImage = card.querySelector('[data-product-card-image-primary]');
      if (primaryImage && swatchBtn.dataset.image) {
        primaryImage.setAttribute('src', swatchBtn.dataset.image);
        primaryImage.setAttribute('alt', swatchBtn.dataset.alt || swatchBtn.dataset.title || '');
        if (swatchBtn.dataset.imageSrcset) {
          primaryImage.setAttribute('srcset', swatchBtn.dataset.imageSrcset);
        } else {
          primaryImage.removeAttribute('srcset');
        }
        if (swatchBtn.dataset.imageSizes) {
          primaryImage.setAttribute('sizes', swatchBtn.dataset.imageSizes);
        } else {
          primaryImage.removeAttribute('sizes');
        }
        primaryImage.setAttribute('loading', 'lazy');
        primaryImage.setAttribute('decoding', 'async');
        primaryImage.setAttribute('fetchpriority', 'low');
      }

      const secondaryImage = card.querySelector('[data-product-card-image-secondary]');
      if (secondaryImage) {
        const secondarySrc = swatchBtn.dataset.secondaryImage || swatchBtn.dataset.image || '';
        const hasSecondaryImage = Boolean(swatchBtn.dataset.secondaryImage);
        card.dataset.hasSecondaryImage = hasSecondaryImage ? 'true' : 'false';
        card.classList.toggle('productCard--hasSecondaryImage', hasSecondaryImage);

        if (secondarySrc) {
          secondaryImage.setAttribute('src', secondarySrc);
          secondaryImage.setAttribute('alt', swatchBtn.dataset.alt || swatchBtn.dataset.title || '');
          secondaryImage.setAttribute('loading', 'lazy');
          secondaryImage.setAttribute('decoding', 'async');
          secondaryImage.setAttribute('fetchpriority', 'low');
          if (hasSecondaryImage && swatchBtn.dataset.secondaryImageSrcset) {
            secondaryImage.setAttribute('srcset', swatchBtn.dataset.secondaryImageSrcset);
          } else if (swatchBtn.dataset.imageSrcset) {
            secondaryImage.setAttribute('srcset', swatchBtn.dataset.imageSrcset);
          } else {
            secondaryImage.removeAttribute('srcset');
          }
          if (hasSecondaryImage && swatchBtn.dataset.secondaryImageSizes) {
            secondaryImage.setAttribute('sizes', swatchBtn.dataset.secondaryImageSizes);
          } else if (swatchBtn.dataset.imageSizes) {
            secondaryImage.setAttribute('sizes', swatchBtn.dataset.imageSizes);
          } else {
            secondaryImage.removeAttribute('sizes');
          }
        }
      }
    }

    const price = card.querySelector('[data-product-card-price]');
    if (price && swatchBtn.dataset.price) {
      price.textContent = swatchBtn.dataset.price;
    }

    const compareWrap = card.querySelector('[data-product-card-compare-wrap]');
    const compare = card.querySelector('[data-product-card-compare]');
    const hasCompare = swatchBtn.dataset.hasCompare === 'true';
    if (compareWrap) {
      compareWrap.hidden = !hasCompare;
    }
    if (compare && hasCompare) {
      compare.textContent = swatchBtn.dataset.compare || '';
    }

    const badge = card.querySelector('[data-product-card-badge]');
    if (badge) {
      badge.hidden = !hasCompare;
      if (hasCompare) {
        const badgeValue = badge.querySelector('[data-product-card-badge-value]');
        if (badgeValue) {
          badgeValue.textContent = swatchBtn.dataset.discountPercentage || '0';
        }
      }
    }

    const installmentsWrap = card.querySelector('[data-product-card-installments-wrap]');
    const installments = card.querySelector('[data-product-card-installments]');
    const popupInstallmentsWrap = card.querySelector('[data-product-card-popup-installments-wrap]');
    const popupInstallments = card.querySelector('[data-product-card-popup-installments]');
    const hasInstallments = Boolean(swatchBtn.dataset.installments);
    if (installmentsWrap) {
      installmentsWrap.hidden = !hasInstallments;
    }
    if (installments && hasInstallments) {
      installments.textContent = swatchBtn.dataset.installments;
    }
    if (popupInstallmentsWrap) {
      popupInstallmentsWrap.hidden = !hasInstallments;
    }
    if (popupInstallments && hasInstallments) {
      popupInstallments.textContent = swatchBtn.dataset.installments;
    }

    const popupTitle = card.querySelector('[data-product-card-popup-title]');
    if (popupTitle && swatchBtn.dataset.title) {
      popupTitle.textContent = swatchBtn.dataset.title;
    }

    const popupPrice = card.querySelector('[data-product-card-popup-price]');
    if (popupPrice && swatchBtn.dataset.price) {
      popupPrice.textContent = swatchBtn.dataset.price;
    }

    const popupDetails = card.querySelector('[data-product-card-popup-details]');
    if (popupDetails && swatchBtn.dataset.url) {
      popupDetails.setAttribute('href', swatchBtn.dataset.url);
    }

    const sizeContainers = card.querySelectorAll('[data-product-card-sizes]');
    if (sizeContainers.length) {
      const template = Array.from(card.querySelectorAll('template[data-product-card-template]'))
        .find((node) => node.dataset.productCardTemplate === handle);

      if (template) {
        sizeContainers.forEach((sizes) => {
          sizes.innerHTML = template.innerHTML;
        });
      }
    }

    this._resetSizeSelection(card);
    this._syncAddButton(card);

    if (this._isCarouselMode(card)) {
      this._syncCarousel(card, handle);
    }
  }

  _setupCarousel(card) {
    if (card.dataset.carouselReady === 'true') return;

    const carousel = card.querySelector('[data-product-card-carousel]');
    const track = card.querySelector('[data-product-card-carousel-track]');
    if (!carousel || !track) return;

    if (card.dataset.carouselDrag === 'true') {
      this._bindCarouselDrag(card, carousel);
    }

    card.dataset.carouselReady = 'true';
  }

  _syncCarousel(card, handle) {
    const template = Array.from(card.querySelectorAll('template[data-product-card-gallery-template]'))
      .find((node) => node.dataset.productCardGalleryTemplate === handle);
    const track = card.querySelector('[data-product-card-carousel-track]');
    const controls = card.querySelector('[data-product-card-carousel-controls]');
    if (!template || !track || !controls) return;

    track.innerHTML = template.innerHTML;
    const slides = Array.from(track.querySelectorAll('[data-product-card-carousel-slide]'));
    slides.forEach((slide, index) => {
      const isActive = index === 0;
      slide.classList.remove('is-leaving', 'is-entering', 'from-next', 'from-prev', 'to-next', 'to-prev');
      slide.classList.toggle('is-active', isActive);
      slide.hidden = !isActive;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    const showArrows = card.dataset.carouselArrows === 'true';
    const hideControls = slides.length <= 1 || !showArrows;
    controls.hidden = hideControls;
    controls.style.display = hideControls ? 'none' : '';
  }


  _bindCarouselDrag(card, carousel) {
    const dragState = {
      active: false,
      moved: false,
      startX: 0,
      startY: 0,
      pointerId: null,
      suppressClick: false,
    };
    const threshold = 18;
    const hasPointerEvents = 'PointerEvent' in window;

    const resetDragState = () => {
      dragState.active = false;
      dragState.moved = false;
      dragState.startX = 0;
      dragState.startY = 0;
      dragState.pointerId = null;
      carousel.classList.remove('is-dragging');
    };

    const beginDrag = (clientX, clientY, pointerId = null) => {
      dragState.active = true;
      dragState.moved = false;
      dragState.startX = clientX;
      dragState.startY = clientY;
      dragState.pointerId = pointerId;
      dragState.suppressClick = false;
      carousel.classList.add('is-dragging');
    };

    const updateDrag = (clientX, clientY) => {
      if (!dragState.active) return false;

      const deltaX = clientX - dragState.startX;
      const deltaY = clientY - dragState.startY;

      if (Math.abs(deltaX) <= threshold || Math.abs(deltaX) <= Math.abs(deltaY)) return false;

      dragState.moved = true;
      dragState.suppressClick = true;
      return true;
    };

    const finishDrag = (clientX, clientY) => {
      if (!dragState.active) return;

      const deltaX = clientX - dragState.startX;
      const deltaY = clientY - dragState.startY;
      const shouldStep = Math.abs(deltaX) > threshold && Math.abs(deltaX) > Math.abs(deltaY);

      if (dragState.moved && shouldStep) {
        this._stepCarousel(card, deltaX < 0 ? 1 : -1);
      }

      window.setTimeout(() => {
        dragState.suppressClick = false;
      }, 0);

      resetDragState();
    };

    carousel.addEventListener('dragstart', (event) => {
      event.preventDefault();
    });

    carousel.querySelectorAll('a, img').forEach((node) => {
      node.setAttribute('draggable', 'false');
    });

    if (hasPointerEvents) {
      carousel.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('[data-product-card-carousel-prev], [data-product-card-carousel-next]')) return;

        beginDrag(event.clientX, event.clientY, event.pointerId);

        if (carousel.setPointerCapture) {
          carousel.setPointerCapture(event.pointerId);
        }
      });

      carousel.addEventListener('pointermove', (event) => {
        if (!dragState.active || dragState.pointerId !== event.pointerId) return;

        if (updateDrag(event.clientX, event.clientY)) {
          event.preventDefault();
        }
      });

      const handlePointerEnd = (event) => {
        if (!dragState.active || dragState.pointerId !== event.pointerId) return;

        if (carousel.releasePointerCapture && carousel.hasPointerCapture?.(event.pointerId)) {
          carousel.releasePointerCapture(event.pointerId);
        }

        finishDrag(event.clientX, event.clientY);
      };

      carousel.addEventListener('pointerup', handlePointerEnd);
      carousel.addEventListener('pointercancel', handlePointerEnd);
    } else {
      carousel.addEventListener('mousedown', (event) => {
        if (event.button !== 0) return;
        if (event.target.closest('[data-product-card-carousel-prev], [data-product-card-carousel-next]')) return;

        beginDrag(event.clientX, event.clientY);
      });

      window.addEventListener('mousemove', (event) => {
        if (!dragState.active) return;

        if (updateDrag(event.clientX, event.clientY)) {
          event.preventDefault();
        }
      });

      window.addEventListener('mouseup', (event) => {
        if (!dragState.active) return;

        finishDrag(event.clientX, event.clientY);
      });
    }

    carousel.addEventListener('click', (event) => {
      if (dragState.suppressClick) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const activeLink = event.target.closest('a[href]') || carousel.querySelector('[data-product-card-carousel-slide].is-active[href]');
      if (!activeLink) return;

      event.preventDefault();
      window.location.assign(activeLink.href);
    }, true);
  }
  _stepCarousel(card, direction) {
    const slides = Array.from(card.querySelectorAll('[data-product-card-carousel-track] [data-product-card-carousel-slide]'));
    if (slides.length < 2) return;

    const currentIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
    const safeIndex = currentIndex >= 0 ? currentIndex : 0;
    const nextIndex = (safeIndex + direction + slides.length) % slides.length;
    if (nextIndex === safeIndex) return;

    const currentSlide = slides[safeIndex];
    const nextSlide = slides[nextIndex];
    const enterClass = direction > 0 ? 'from-next' : 'from-prev';
    const leaveClass = direction > 0 ? 'to-prev' : 'to-next';

    slides.forEach((slide) => {
      slide.classList.remove('is-entering', 'is-leaving', 'from-next', 'from-prev', 'to-next', 'to-prev');
    });

    nextSlide.hidden = false;
    nextSlide.setAttribute('aria-hidden', 'false');
    nextSlide.classList.add('is-entering', enterClass);

    currentSlide.hidden = false;
    currentSlide.setAttribute('aria-hidden', 'true');
    currentSlide.classList.add('is-leaving', leaveClass);

    requestAnimationFrame(() => {
      nextSlide.classList.add('is-active');
      nextSlide.classList.remove('from-next', 'from-prev');

      currentSlide.classList.remove('is-active');
      currentSlide.classList.remove('to-next', 'to-prev');
    });

    window.setTimeout(() => {
      currentSlide.hidden = true;
      currentSlide.classList.remove('is-leaving');
      nextSlide.classList.remove('is-entering');
    }, 520);
  }

  _selectSize(card, sizeBtn) {
    if (sizeBtn.disabled) return;

    const variantId = sizeBtn.dataset.variantId || '';
    if (!variantId) return;

    card.dataset.selectedVariantId = variantId;

    card.querySelectorAll('[data-size-option]').forEach((button) => {
      button.classList.toggle('is-active', button.dataset.variantId === variantId);
    });

    this._syncAddButton(card);

    if (card.hasAttribute('data-size-quick-add') && !this._isMobileViewport()) {
      this._addToCart(card, sizeBtn, variantId);
    }
  }

  _resetSizeSelection(card) {
    const sizeOptions = Array.from(card.querySelectorAll('[data-size-option]'));
    if (!sizeOptions.length) {
      return;
    }

    sizeOptions.forEach((button) => {
      button.classList.remove('is-active');
    });

    const availableVariantIds = [...new Set(sizeOptions.filter((button) => !button.disabled).map((button) => button.dataset.variantId).filter(Boolean))];
    if (availableVariantIds.length === 1) {
      const onlyVariantId = availableVariantIds[0];
      card.dataset.selectedVariantId = onlyVariantId;
      sizeOptions.forEach((button) => {
        button.classList.toggle('is-active', button.dataset.variantId === onlyVariantId);
      });
      return;
    }

    card.dataset.selectedVariantId = '';
  }

  _syncAddButton(card) {
    const addButtons = card.querySelectorAll('[data-product-card-add]');
    if (!addButtons.length) return;

    const variantId = card.dataset.selectedVariantId || '';
    const isMobileQuickAddCard = this._isMobileQuickAddCard(card);
    addButtons.forEach((addBtn) => {
      const isMobileTrigger = isMobileQuickAddCard && addBtn.hasAttribute('data-mobile-quick-add-trigger');
      addBtn.disabled = isMobileTrigger ? false : !variantId;
      addBtn.setAttribute('aria-disabled', (isMobileTrigger || variantId) ? 'false' : 'true');
    });
  }

  async _handleAddToCart(card, btn) {
    const variantId = card.dataset.selectedVariantId || '';
    if (!variantId || btn.disabled) return;
    await this._addToCart(card, btn, variantId);
  }

  async _addToCart(card, btn, variantId) {
    if (card.dataset.quickAddBusy === 'true') return;
    card.dataset.quickAddBusy = 'true';

    btn.style.width = `${btn.offsetWidth}px`;
    btn.style.height = `${btn.offsetHeight}px`;

    const result = await window.CartManager?.add(variantId, { button: btn });

    btn.style.width = '';
    btn.style.height = '';
    delete card.dataset.quickAddBusy;

    if (result?.success) {
      this._resetSizeSelection(card);
      this._syncAddButton(card);
      this._setSizePopupState(card, false);
    }
  }

  destroy() {
    document.removeEventListener('click', this._onClick);
  }
}

const bootProductManager = () => {
  if (!window.ProductManager) {
    window.ProductManager = new ProductManager();
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootProductManager, { once: true });
} else {
  bootProductManager();
}






